import Vue from 'vue';
import Router from 'vue-router';
import nextFactory from '@/middleware/nextFactory';
import { store } from '@/store';
import vuexStore from '@/store/store';
import { checkIfNewAppVersionExists } from '@/shared/versionChecker';
import { PAGES_NOT_NEED_CHECK_VERSION } from '@/shared/constants';
import { getParamVersion } from '@/shared/version';
import { getValidations } from '@/cruds/validations.crud';
import { transportRoutes } from '@/modules/transports/route';
import { documentPackageRoutes } from '@/modules/document-package/route';
import { Capacitor } from '@capacitor/core';
import { carbonRemovalRoutes } from '@/modules/carbon-removal/route';
import { onboardingRoutes } from '@/modules/onboarding/route';
import { carbonClapRoutes } from '@/modules/carbonclap/route';
import { fimalacRoutes } from '@/modules/fimalac/route';
import manageBudgetMiddleware from '@/middleware/manageBudgetMiddleware';
import canAccessToAppMiddleware from '../middleware/canAccessToAppMiddleware';
import packageJson from './../../package.json';
import * as _ from 'lodash';

// Containers for appiteasy
const DefaultContainer = () => import('@/containers/DefaultContainer');

// Views
const OnboardingHome = () => import('@/views/OnboardingHome');
const Dashboard = () => import('@/views/Dashboard');
const Test = () => import('@/views/Test');
const Translations = () => import('@/views/translation/translation');
const BudgetPOList = () => import('@/views/budget/BudgetPOList');

const Colors = () => import('@/views/theme/Colors');
const Typography = () => import('@/views/theme/Typography');

const Widgets = () => import('@/views/Widgets');

const Profile = () => import('@/views/Profile');
const MyContracts = () => import('@/views/mycontracts');
const ChangePassword = () => import('@/views/users/ChangePassword');

// components
const CapturePicture = () => import('@/components/CapturePicture');
const CropPicture = () => import('@/components/CropPicture');

// Views - Pages
const Page404 = () => import('@/views/pages/Page404');
const Page500 = () => import('@/views/pages/Page500');
const Login = () => import('@/views/pages/Login');
const Register = () => import('@/views/pages/Register');
const NewRegister = () => import('@/views/pages/NewRegister');
const PageOodriveSuccess = () => import('@/views/pages/oodrive-success');
const PageOodriveFailure = () => import('@/views/pages/oodrive-failure');

// Users
const Users = () => import('@/views/users/Users');
const User = () => import('@/views/users/User');
const NewUser = () => import('@/views/users/UserForm');
// contacts
const Contacts = () => import('@/views/users/Contacts');

// Project administration
const ProjectAdministration = () => import('@/views/ProjectAdministration');
// Projects
const Projects = () => import('@/views/projects/Projects');
const ProjectForm = () => import('@/views/projects/ProjectForm');
const MyProject = () => import('@/views/projects/MyProject');

const MemoDeals = () => import('@/views/projects/MemoDeals');
// Timesheet
const Work = () => import('@/views/timesheet/Work');
// Stage days
const StageDay = () => import('@/views/stageday/stageday');

const MyTimesheets = () => import('@/views/mytimesheets/MyTimesheets');
const TimesheetDetails = () => import('@/views/mytimesheets/TimesheetDetails');
const GreenReportScreen = () => import('@/views/green/Green');
const Waste = () => import('@/views/waste/Waste');

const ValidateTimesheets = () => import('@/views/validateTS/ValidateTimesheets');
const ValidateTSDetails = () => import('@/views/validateTS/ValidateTSDetails');

// PO
const MyPo = () => import('@/views/mypo/MyPo');

const LocationsIndex = () => import('@/views/locations/index');
const LocationsId = () => import('@/views/locations/_id');

// Expenses
const MyExpenses = () => import('@/views/myexpenses/MyExpenses');
const ExpenseDetails = () => import('@/views/myexpenses/ExpenseDetails');

// Expense Forms
const ExpenseForm = () => import('@/components/MyExpenseMobile/ExpenseForm');

const ValidateExpenses = () => import('@/views/validateExp/ValidateExpenses');
const ValidateExpDetails = () => import('@/views/validateExp/ValidateExpDetails');

const ValidatePOs = () => import('@/views/validatePO/ValidatePOs');
const ValidatePoDetails = () => import('@/views/validatePO/ValidatePoDetails');

// Documents
const Documents = () => import('@/views/documents/documents');
const DocumentDetails = () => import('@/views/documents/documentDetails');
const MyDocumentDetails = () => import('@/views/documents/MyDocumentDetails');
const Deliveries = () => import('@/views/documents/deliveries');
const MyDocuments = () => import('@/views/documents/mydocuments');

// Tip of the day
const TipOfDay = () => import('@/views/tod/tipofday');
const MyTipOfDay = () => import('@/views/tod/mytipofday');

// Green Film
const GreenFilmPrerequisite = () => import('@/views/greenfilm/prerequisite');
const GreenFilmDocs = () => import('@/components/greenfilm/greenfilmdocs');
const GreenFilmFinalReport = () => import('@/views/greenfilm/finalreport');

// Albert
const AlbertProd = () => import('@/views/albert/albertprod');
const AlbertActionPlan = () => import('@/views/albert/albertactionplan');
const AlbertActionPlanSection = () => import('@/views/albert/albertactionplan/Section');
const AlbertActionReport = () => import('@/views/albert/albertactionreport');
const AlbertCarbonFootprint = () => import('@/views/albert/albertcarbonfootprint');

// green suppliers
const LoadGreenSuppliers = () => import('@/views/suppliergreen/loadgreensupplier');

// Accomodations
const AccomodationList = () => import('@/views/accomodation/accomodationList');
const Accomodations = () => import('@/views/accomodation/accomodations');
const Distribution = () => import('@/views/accomodation/distribution');
const MyAccomodations = () => import('@/views/accomodation/myaccomodations');

// Licenses
const LicenseList = () => import('@/views/license/licenseList');
const License = () => import('@/views/license/license');

// Organisation
const Organisation = () => import('@/views/Organisation');

// Validation
const Validations = () => import('@/views/Validations');

const Expenses = () => import('@/views/Expenses');
const Timesheets = () => import('@/views/Timesheets');
const Exports = () => import('@/views/Exports');
const Imports = () => import('@/views/Imports');
const BudgetGlobal = () => import('@/views/budget/BudgetGlobal');
const BudgetForOneDepartment = () => import('@/views/budget/BudgetForOneDepartment');
const PoDetails = () => import('@/views/budget/PoDetails');
const TotalSalariesForOneDepartment = () => import('@/views/budget/TotalSalariesForOneDepartment');
const TimesheetsOfUser = () => import('@/views/budget/TimesheetsOfUser');

const ExpenseItemsDepCat = () => import('@/views/budget/expenseItemsDepCat');
const Category = () => import('@/views/budget/NewCategoryScreen');

const Department = () => import('@/views/department/department');
const Function = () => import('@/views/department/function');
const MyDashboard = () => import('@/views/MyDashboard');

const Testscreen = () => import('@/views/testscreen');

const RedirectSubscription = () => import('@/views/RedirectSubscription');

// Task types
// const TaskTypes = () =>

// Teams

Vue.use(Router);

const router = new Router({
	mode: 'hash', // https://router.vuejs.org/api/#mode
	linkActiveClass: 'open active',
	scrollBehavior: () => ({ y: 0 }),
	routes: [
		{
			path: '/',
			redirect: '/pages/login',
			name: 'TheGreenShot',
			component: DefaultContainer,
			children: [
				...fimalacRoutes,
				{
					path: 'dashboard',
					name: 'Dashboard',
					meta: {
						middleware: canAccessToAppMiddleware
					},
					component: Dashboard
				},
				{
					path: 'home',
					name: 'Home',
					meta: {
						middleware: canAccessToAppMiddleware
					},
					component: OnboardingHome
				},
				{
					path: 'my-dashboard',
					name: 'My Dashboard',
					meta: {
						middleware: canAccessToAppMiddleware
					},
					component: OnboardingHome
				},
				{
					path: 'myproject',
					name: 'My Project',
					meta: {
						middleware: canAccessToAppMiddleware
					},
					component: MyProject
				},
				{
					path: 'settings',
					meta: {
						label: 'Settings',
						middleware: canAccessToAppMiddleware
					},
					component: ProjectAdministration
				},
				{
					path: 'profile',
					meta: {
						label: 'Profile',
						middleware: canAccessToAppMiddleware
					},
					component: {
						render(c) {
							return c('router-view');
						}
					},
					children: [
						{
							path: '',
							name: 'Profile',
							meta: {
								middleware: canAccessToAppMiddleware
							},
							component: Profile
						},
						{
							path: 'CropPicture',
							name: 'Crop Picture',
							meta: {
								middleware: canAccessToAppMiddleware
							},
							component: CropPicture
						}
					]
				},
				{
					path: 'mycontracts',
					meta: {
						label: 'My Contracts',
						middleware: canAccessToAppMiddleware
					},
					component: {
						render(c) {
							return c('router-view');
						}
					},
					children: [
						{
							path: '',
							name: 'My Contracts',
							meta: {
								middleware: canAccessToAppMiddleware
							},
							component: MyContracts
						}
					]
				},
				{
					path: 'translation',
					meta: {
						label: 'Translations',
						middleware: canAccessToAppMiddleware
					},
					component: Translations,
					name: 'Translation'
				},
				{
					path: 'users',
					meta: {
						label: 'Users',
						middleware: canAccessToAppMiddleware
					},
					component: {
						render(c) {
							return c('router-view');
						}
					},
					children: [
						{
							path: '',
							component: Users,
							meta: {
								middleware: canAccessToAppMiddleware
							},
							name: 'All Users'
						},
						{
							path: 'new',
							meta: {
								label: 'New User',
								middleware: canAccessToAppMiddleware
							},
							name: 'New User',
							component: NewUser
						},
						{
							path: ':id',
							meta: {
								label: 'User Details',
								middleware: canAccessToAppMiddleware
							},
							name: 'User details',
							component: User
						}
					]
				},
				{
					path: 'scripts',
					meta: { label: 'Scripts', middleware: canAccessToAppMiddleware },
					component: {
						render(c) {
							return c('router-view');
						}
					},
					children: [
						{
							path: '',
							component: () => import('@/views/script/index.vue'),
							meta: { label: 'List' }
						},
						{
							path: ':action/:id',
							component: () => import('@/views/script/_id.vue'),
							meta: { label: 'Form' }
						}
					]
				},
				{
					path: 'allUsers',
					meta: {
						label: 'Users',
						middleware: canAccessToAppMiddleware
					},
					component: {
						render(c) {
							return c('router-view');
						}
					},
					children: [
						{
							path: '',
							component: Users,
							meta: {
								middleware: canAccessToAppMiddleware
							},
							name: 'All App Users'
						}
					]
				},
				{
					path: 'licenseList',
					meta: {
						label: 'License list',
						middleware: canAccessToAppMiddleware
					},
					component: {
						render(c) {
							return c('router-view');
						}
					},
					children: [
						{
							path: '',
							component: LicenseList,
							name: 'License list',
							meta: {
								middleware: canAccessToAppMiddleware
							}
						},

						{
							path: ':id',
							component: License,
							name: 'License',
							meta: {
								middleware: canAccessToAppMiddleware
							}
						},
						{
							path: ':licenseId/newProject',
							meta: { label: 'New project for license' },
							name: 'AddProjectForLicenseFromDev',
							component: ProjectForm
						},
						{
							path: ':licenseId/updProject/:projectId',
							meta: { label: 'Update project', middleware: canAccessToAppMiddleware },
							name: 'UpdateProjectForLicenseFromDev',
							component: ProjectForm
						}
					]
				},
				{
					path: 'Locations',
					meta: {
						label: 'Locations',
						middleware: canAccessToAppMiddleware
					},
					component: {
						render(c) {
							return c('router-view');
						}
					},
					children: [
						{
							path: '',
							meta: {
								middleware: canAccessToAppMiddleware
							},
							component: LocationsIndex,
							name: 'Locations list'
						},
						{
							path: ':id',
							meta: {
								middleware: canAccessToAppMiddleware
							},
							component: LocationsId,
							name: 'Location'
						}
					]
				},
				{
					path: 'imports',
					meta: {
						label: 'Imports',
						middleware: canAccessToAppMiddleware
					},
					component: {
						render(c) {
							return c('router-view');
						}
					},
					children: [
						{
							path: '',
							meta: {
								middleware: canAccessToAppMiddleware
							},
							component: Imports,
							name: 'Imports'
						}
					]
				},
				{
					path: 'projects',
					meta: {
						label: 'Projects',
						middleware: canAccessToAppMiddleware
					},
					component: {
						render(c) {
							return c('router-view');
						}
					},
					children: [
						{
							path: '',
							component: Projects,
							meta: { middleware: canAccessToAppMiddleware }
						},
						{
							path: 'new',
							meta: { label: 'New Project', middleware: canAccessToAppMiddleware },
							name: 'NewProject',
							component: ProjectForm
						},

						{
							path: ':id/update',
							meta: { label: 'Update Project', middleware: canAccessToAppMiddleware },
							name: 'UpdateProject',
							component: ProjectForm
						},
						{
							path: ':id/users',
							meta: { label: 'Project Users', middleware: canAccessToAppMiddleware },
							name: 'ProjectUsers',
							component: Users
						},
						{
							path: ':id/adduser',
							meta: { label: 'Add User', middleware: canAccessToAppMiddleware },
							name: 'AddUser',
							component: NewUser
						},
						{
							path: ':projectId',
							meta: { label: 'Project Details', middleware: canAccessToAppMiddleware },
							name: 'UpdateProjectFromProjects',
							component: ProjectForm
						}
					]
				},
				{
					path: 'departments',
					meta: {
						label: 'Departments',
						middleware: canAccessToAppMiddleware
					},
					component: {
						render(c) {
							return c('router-view');
						}
					},
					children: [
						{
							path: '',
							component: Department,
							name: 'Departments',
							meta: { middleware: canAccessToAppMiddleware }
						},
						{
							path: ':department/functions',
							meta: { label: 'Functions', middleware: canAccessToAppMiddleware },
							name: 'Functions for one department',
							component: Function
						}
					]
				},
				{
					path: 'project',
					redirect: '/projects/0', // redirect to current project details if click on project link instead of blank page
					meta: { label: 'Project', middleware: canAccessToAppMiddleware },
					component: {
						render(c) {
							return c('router-view');
						}
					},
					children: [
						{
							path: 'users',
							meta: {
								label: 'Users',
								middleware: canAccessToAppMiddleware
							},
							component: Users,
							name: 'Users'
						},
						{
							path: 'MemoDeals',
							meta: { label: 'Memo deals', middleware: canAccessToAppMiddleware },
							component: MemoDeals,
							name: 'MemoDeals'
						},
						{
							path: 'users/:id',
							meta: { label: 'User', middleware: canAccessToAppMiddleware },
							component: User,
							name: 'User'
						},
						{
							path: 'organisation',
							name: 'Organisation',
							component: Organisation,
							meta: { middleware: canAccessToAppMiddleware }
						},
						{
							path: 'validations',
							name: 'Validations',
							meta: {
								middleware: canAccessToAppMiddleware
							},
							component: Validations
						}
					]
				}
			]
		},
		{
			path: '/loading',
			name: 'loading',
			component: () => import('@/views/pages/loading.vue')
		},
		{
			path: '/loading-new',
			name: 'loading-new',
			component: () => import('@/views/pages/loading-new.vue')
		},
		{
			path: '/pages/redirect-subscription',
			name: 'redirect-subscription',
			component: RedirectSubscription
		},
		{
			path: '/pages',
			redirect: '/pages/404',
			name: 'Pages',
			component: {
				render(c) {
					return c('router-view');
				}
			},

			children: [
				{
					path: '404',
					name: 'Page404',
					component: Page404
				},
				{
					path: '500',
					name: 'Page500',
					component: Page500
				},
				{
					path: 'login',
					name: 'Login',
					component: Login
				},
				{
					path: 'register',
					name: 'Register',
					// component: Register
					component: NewRegister
				}
			]
		},
		{ path: '*', redirect: '/pages/404' },
		{
			path: '/oodrive-success',
			name: 'Page Success For Signature',
			component: PageOodriveSuccess
		},
		{
			path: '/oodrive-failure',
			name: 'Page Failure For Signature',
			component: PageOodriveFailure
		}
	]
});

const DEFAULT_TITLE = 'TheGreenShot';

router.beforeResolve((to, from, next) => {
	// If this isn't an initial page load.
	if (to.name) {
		// Start the route progress bar.
		NProgress.start();
	}
	next();
});

router.afterEach((to, from) => {
	if (to.name) {
		document.title = DEFAULT_TITLE + ' - ' + to.name;
	} else {
		document.title = DEFAULT_TITLE;
	}
	NProgress.done();
});

const isInviteCrew = () => {
	const url = window.location.hash.replace('#', '').trim();
	const isInvite = url.toString().includes('?NU=');
	return {
		url,
		isInvite
	};
};

const configRoutes = () => {
	router.beforeEach(async (to, from, next) => {
		await vuexStore.restored;
		const queryName = to.query.NU;
		const { getters } = await vuexStore;
		const version = getParamVersion();
		const defaultAuth = 'Login';
		const dashboard = 'My Dashboard';
		const planning = 'fimalacTimecardPlanning';
		const token = getters['config/getToken'];
		const isShowFimalacPlanning = getters['config/isShowFimalacPlanning'];
		const isConnected = token ? token : null;

		const NODE_ENV = process.env.NODE_ENV || 'development';
		if (NODE_ENV === 'production') {
			const currentClientVersion = await vuexStore.getters['version/getClientVersion'];
			if (!currentClientVersion) {
				await vuexStore.dispatch('version/setNewVersionStates', { version: packageJson.version, needUpdate: false });
			}
			const canCheckVersion = PAGES_NOT_NEED_CHECK_VERSION.every((text) => !from.fullPath.includes(text) && !to.fullPath.includes(text));
			if (canCheckVersion) {
				const isNewVersion = await checkIfNewAppVersionExists();
				if (isNewVersion) return false;
			}
		}

		if (Capacitor.getPlatform() !== 'web') {
			next();
		}
		if (queryName) {
			next();
		} else {
			if (to.name !== defaultAuth) {
				if (!isConnected) {
					next({ name: defaultAuth });
				} else {
					if (queryName) {
						next({ name: defaultAuth });
					} else {
						const forURL = isShowFimalacPlanning ? planning : dashboard;
						if (to.fullPath === '/') {
							if (forURL === dashboard) {
								next({ name: forURL, query: { v: version } });
							} else {
								next({ name: forURL });
							}
						}
					}
				}
			} else {
				if (isConnected) {
					const queryDefault = window.location.href.toString().split('?NU=')[1];
					const queryValue = queryDefault ? queryDefault.substring(0, 20) : null;
					const forURL = isShowFimalacPlanning ? planning : dashboard;
					if (to.meta.middleware) {
						const middleware = Array.isArray(to.meta.middleware) ? to.meta.middleware : [to.meta.middleware];

						const context = {
							from,
							next,
							router,
							to,
							localStore: store
						};

						const nextMiddleware = nextFactory(context, middleware, 1);
						return middleware[0]({ ...context, next: nextMiddleware });
					}
					if (queryValue) {
						return next({ name: defaultAuth, query: { NU: queryValue } });
					} else if (forURL === dashboard) {
						return next({ name: forURL, query: { v: version } });
					}
					return next({ name: forURL });
				}
			}
			next();
		}
	});
};

// (async () => {
// const { dispatch, getters } = await vuexStore;
// const isLoad = getters['config/isLoaded'];
// const isShowFimalacPlanning = getters['config/isShowFimalacPlanning'];
// const isToken = getters['config/isToken'];
// const defaultAuth = 'Login';
// if (isToken) {
// 	if (isLoad) {
// 		const { isInvite } = isInviteCrew();
// 		const planning = '/time-cards/planning';
// 		const isAuthorize = isShowFimalacPlanning; // await isShowFimalacPlanning();
// 		const alreadyUrlPlanning = window.location.hash.includes(planning);
// 		if (!isInvite) {
// 			if (isAuthorize && !alreadyUrlPlanning) {
// 				router.push(planning);
// 			} else {
// 				router.push('/my-dashboard');
// 			}
// 		} else {
// 			router.push('/my-dashboard');
// 		}
// 	} else {
// 		router.push('/my-dashboard');
// 	}
// } else {
// 	router.push(`/${defaultAuth}`);
// }
// })();

configRoutes();
export default router;
